// Visit The Stimulus Handbook for more details
// https://stimulusjs.org/handbook/introduction
//
// This example controller works with specially annotated HTML like:
//
// <div data-controller="donation">
//   <h1 data-target="donation.output"></h1>
// </div>

import {
  Controller
} from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    'amount', 'splitRatio', 'matchAmount', 'matchPercentage', 'matchAmountLow', 'matchAmountTop',
    'boxLow', 'totalAmount', 'donationAmount', 'boxAmountHigh', 'charityNameLow', 'charityNameHigh',
    'lowPercentage', 'highPercentage', 'referralCode', 'referralCodeStatus', 'matchingRate',
    'matchReferralPercentage', 'matchNormalPercentage', 'matchReferral', 'highAdonationAmount',
    'aiButton', 'aiButtonNote', 'donationFrequency'
  ]

  connect() {
    this.config = JSON.parse(this.element.dataset.config);
    this.normal = JSON.parse(this.element.dataset.normal);
    this.update()
    if(this.hasReferralCodeTarget && this.referralCodeTarget.value != '')
      this.validateCode()
  }

  validAmount() {
    return !isNaN(parseInt(this.amountTarget.value, 10)) && parseInt(this.amountTarget.value, 10) >= 0;
  }

  splitRatio() {
    return parseFloat(this.splitRatioTarget.value, 10)
  }

  matchPercentage() {
    let ratio = this.config[this.splitRatio().toFixed(1)]
    return parseInt(ratio * 100);
  }

  matchNormalPercentage() {
    let ratio = this.normal[this.splitRatio().toFixed(1)]
    return parseInt(ratio * 100);
  }

  matchReferralPercentage() {
    return this.matchPercentage() - this.matchNormalPercentage()
  }

  lowPercentage() {
    return 100 - this.highPercentage()
  }

  highPercentage() {
    return this.splitRatio() * 100
  }

  highAdonationAmount() {
    return this.donationAmount() * this.highPercentage() / 100;
  }

  donationAmount() {
    if (!this.validAmount()) {
      return 0;
    }
    return parseInt(this.amountTarget.value, 10);
  }

  totalAmount() {
    if (!this.validAmount()) {
      return 0;
    }
    return this.donationAmount() + this.matchAmount();
  }

  matchAmount() {
    if (!this.validAmount()) {
      return 0;
    }
    return Math.floor(this.donationAmount() * this.matchPercentage()) / 100.0;
  }

  matchAmountTop() {
    return this.totalAmount() - this.matchAmountLow();
  }

  matchAmountLow() {
    return Math.floor(this.totalAmount() * this.lowPercentage()) / 100.0;
  }

  highCharitySelected() {
    var e = document.querySelector('input[name="donation[top_charity_id]"]:checked') ||
            document.getElementById('donation_top_charity_name')
    return !!e?.dataset?.name;
  }

  highCharityName() {
    var e = document.querySelector('input[name="donation[top_charity_id]"]:checked') ||
            document.getElementById('donation_top_charity_name')
    if (e) {
      return e.dataset['name'];
    } else {
      return 'a super-effective charity';
    }
  }

  lowCharitySelected() {
    var e = document.getElementById("donation_standard_charity_name");
    return !!e?.dataset?.name;
  }

  frequency() {
    var e = document.getElementById("donation_frequency");
    if (e.value) {
      return e.value;
    } else {
      return 'once';
    }
  }

  lowCharityName() {
    var e = document.getElementById("donation_standard_charity_name");
    if (e.dataset.id) {
      return e.dataset.name;
    } else {
      return 'your favorite charity';
    }
  }

  submit(e) {
    if (typeof ga === "function") {
      ga('send', 'event', 'DonationForm', 'submit');
    }
  }

  updateSimple() {
    this.matchAmountTarget.innerHTML = this.matchAmount().toFixed(2)
    this.matchAmountLowTarget.innerHTML = this.matchAmountLow().toFixed(2);
    this.matchAmountTopTarget.innerHTML = this.matchAmountTop().toFixed(2);
    this.charityNameLowTargets.forEach((target) => { target.innerHTML = this.lowCharityName(); });
    this.charityNameHighTargets.forEach((target) => { target.innerHTML = this.highCharityName(); });
  }

  update() {
    if(!this.hasMatchPercentageTarget){ this.updateSimple(); return }

    if(this.hasAiButtonTarget) {
      if(this.matchAmount() && this.highCharitySelected() && (this.lowCharitySelected() || this.splitRatio().toFixed(1) == 1.0)) {
        this.aiButtonTarget.disabled = false
        this.aiButtonNoteTarget.classList.add('hidden')
      } else {
        this.aiButtonTarget.disabled = true
        this.aiButtonNoteTarget.classList.remove('hidden')
      }
    }

    let original_matching = this.matchPercentageTarget.innerHTML;
    this.matchAmountTarget.innerHTML = this.matchAmount().toFixed(2)
    this.donationAmountTarget.innerHTML = this.donationAmount().toFixed(2)
    if (this.normal) {
      this.matchNormalPercentageTarget.innerHTML = this.matchNormalPercentage() + '%';
      this.matchReferralPercentageTarget.innerHTML = this.matchReferralPercentage() + '%';
      this.matchReferralTargets.forEach((target) => { target.classList.remove('hidden') });
    } else {
      this.matchReferralTargets.forEach((target) => { target.classList.add('hidden') });
    }
    this.matchPercentageTarget.innerHTML = this.matchPercentage() + '%';
    this.matchAmountLowTarget.innerHTML = this.matchAmountLow().toFixed(2);
    this.matchAmountTopTarget.innerHTML = this.matchAmountTop().toFixed(2);
    this.totalAmountTarget.innerHTML = this.totalAmount().toFixed(2);
    this.lowPercentageTarget.innerHTML = this.lowPercentage().toFixed(0);
    this.highPercentageTarget.innerHTML = this.highPercentage().toFixed(0);

    this.charityNameLowTargets.forEach((target) => { target.innerHTML = this.lowCharityName(); });
    this.charityNameHighTargets.forEach((target) => { target.innerHTML = this.highCharityName(); });
    this.highAdonationAmountTarget.innerHTML = this.highAdonationAmount().toFixed(0);

    if(this.hasDonationFrequencyTarget)
      this.donationFrequencyTarget.innerHTML = this.frequency()

    if(original_matching != this.matchPercentageTarget.innerHTML) {
      this.matchPercentageTarget.classList.add('animate__animated', 'animate__tada');

      this.matchPercentageTarget.addEventListener('animationend', () => {
        this.matchPercentageTarget.classList.remove('animate__animated', 'animate__tada');
      });
    }
  }

  validateCode() {
    if(this.validateCodeAjax){
      this.validateCodeAjax.abort();
      this.validateCodeAjax = null;
    }
    this.validateCodeAjax = this.getAjaxValidateCode(
      this.element.dataset.validateCodePath + '?code=' + this.referralCodeTarget.value
    )
  }

  getAjaxValidateCode(url) {
    let xhr = window.XMLHttpRequest ? new XMLHttpRequest() : new ActiveXObject('Microsoft.XMLHTTP');
    xhr.open('GET', url);
    xhr.onreadystatechange = () => {
      if (xhr.readyState > 3) {
        if (xhr.status == 200) {
          let result = JSON.parse(xhr.responseText);
          this.config = result.config;
          this.normal = result.normal;
          this.referralCodeStatusTarget.innerHTML = result.status;
          this.update()
        }
      }
    }
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    xhr.send();
    return xhr;
  }

  ai_chat(event) {
    event.preventDefault();
    const chat = document.getElementById('ai_chat');
    const chatForm = document.getElementById('ai_chat_form');
    chat.classList.remove('hidden');

    document.getElementById('ai_chat_top_charity_name').value = this.highCharityName();
    document.getElementById('ai_chat_favorite_charity_name').value = this.lowCharityName();
    document.getElementById('ai_chat_split_ratio').value = this.splitRatio();
    document.getElementById('ai_chat_amount').value = this.donationAmount();

    chatForm.requestSubmit()
    chat.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
